/**
 * El saldo de Smarty Drop, a un clic desde el encabezado.
 *
 * POR QUÉ EXISTE. El founder: «actualmente se debe ir hasta Dropshipping y Saldo, pero debe existir
 * algo tipo atajo que se muestre como icono en el header».
 *
 * Tiene razón por una razón concreta: el saldo NO es una pantalla, es un dato que se consulta a
 * mitad de otra tarea —mientras armás un pedido, mientras mirás el catálogo— y que decide si podés
 * seguir. Un dato así vive en el encabezado, al lado de los chats y las notificaciones, no a dos
 * clics de profundidad en un submenú.
 *
 * 🪤 SÓLO APARECE SI TIENE SENTIDO: si el negocio nunca tocó Smarty Drop, un ícono de billetera en
 *    el encabezado es ruido permanente. Se muestra cuando hay saldo o movimientos.
 *
 * 🪤 Y NO REPITE EL NÚMERO EN DOS LUGARES CON DOS CONSULTAS: usa la MISMA clave de caché que la
 *    página de Saldo (`CLAVES_DROP.miSaldo`), así los dos siempre dicen lo mismo y la
 *    segunda lectura no cuesta una consulta más.
 */
import { useState } from 'react';
import { useDatosCacheados } from '@/hooks/useDatosCacheados';
import { Popover, Box, Text, InlineStack, BlockStack, Divider, Icon } from '@/lib/polaris';
import { CashDollarIcon } from '@/lib/polaris-icons';
import { AppButton } from '@/components/admin/AppButton';
import { useAuth } from '@smarty/shared';
import { useAdministraLaPlata } from '@/hooks/useAdministraLaPlata';
import { useStoreNavigate } from '@/hooks/useStoreNavigate';
import { ROUTES } from '@/config/routes.config';
import { Z_SOBRE_EL_PANEL } from '@/components/layout/PanelDelEncabezado';
import { miSaldoDrop, type SaldoDrop } from '@/lib/dropshipping/datosDeDrop';
import { RecargarSaldoModal } from '@/components/dropshipping/RecargarSaldoModal';
import { CLAVES_DROP } from '@/lib/dropshipping/clavesDeCache';

const CURRENCY = new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' });

export function BilleteraMenu() {
  const { tenantId, loading: authLoading } = useAuth();
  const administraLaPlata = useAdministraLaPlata();
  const navigate = useStoreNavigate();
  const [abierto, setAbierto] = useState(false);
  const [recargando, setRecargando] = useState(false);

  const { datos: saldo } = useDatosCacheados<SaldoDrop | null>({
    clave: [CLAVES_DROP.miSaldo, tenantId],
    activo: !authLoading && !!tenantId,
    frescura: 60_000,
    traer: () => miSaldoDrop(tenantId!),
  });

  /*
   * Sin relación con Smarty Drop, el ícono no va: sería ruido permanente en el encabezado.
   *
   * 🪤 PERO UN PEDIDO FRENADO LO TRAE DE VUELTA aunque el saldo esté en cero y no haya movimientos.
   *    Ese es exactamente el caso que hay que avisar: pidió, no pagó, y el paquete no sale. Esconder
   *    la billetera justo ahí sería esconder el aviso.
   */
  const frenados = saldo?.pedidos_frenados ?? 0;
  if (!saldo || (saldo.saldo === 0 && saldo.movimientos === 0 && frenados === 0)) return null;

  /*
   * ⭐ ES UN SEMÁFORO, NO UN CONTADOR. El monto solo no contesta la pregunta con la que se mira el
   *    encabezado: «¿puedo operar?». El color sí.
   *
   *   atención → hay pedidos frenados esperando plata suya
   *   espera   → hay una recarga en revisión: algo está llegando
   *   normal   → puede operar
   *
   * 🪤 El monto se queda igual: quitarlo obligaría a abrir el menú para saber si te alcanza, que es
   *    justo lo que este atajo vino a evitar.
   */
  const falta = Math.max(0, Number(saldo.falta_total ?? 0) - saldo.saldo);
  const estado: 'atencion' | 'espera' | 'normal' =
    frenados > 0 && falta > 0 ? 'atencion' : frenados > 0 ? 'espera' : 'normal';
  const colorPunto = estado === 'atencion' ? '#ff8a80' : '#ffd79d';

  const ir = (ruta: string) => { setAbierto(false); navigate(ruta); };
  // «Recargar» abre el modal acá mismo, no navega: el founder lo apretó y lo mandó a la Billetera.
  const recargar = () => { setAbierto(false); setRecargando(true); };

  return (
    <>
    <Popover
      active={abierto}
      preferredAlignment="right"
      zIndexOverride={Z_SOBRE_EL_PANEL}
      onClose={() => setAbierto(false)}
      activator={
        <button
          type="button"
          onClick={() => setAbierto((v) => !v)}
          aria-label={`Tu saldo: ${CURRENCY.format(saldo.saldo)}`}
          /*
           * 🪤 LA BARRA OSCURA NO ES UNA SUPERFICIE POLARIS. Los tonos de `Text` e `Icon` están
           *    calculados para fondo claro: acá el monto salía negro sobre negro y casi no se
           *    leía —el founder lo vio y mandó la captura—. Por eso TODO lo que vive en esta barra
           *    (el wordmark, el badge de Ctrl+K, los íconos) fija su blanco a mano, y esto también.
           *    El color vive en `.sf-topbar-wallet`, al lado de las otras reglas de la barra, para
           *    que el próximo que agregue algo acá lo encuentre en vez de repetir el bug.
           */
          className="sf-topbar-icon-button sf-topbar-wallet"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            height: 36, padding: '0 10px', border: 'none', background: 'transparent',
            cursor: 'pointer', borderRadius: 8,
          }}
        >
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            <Icon source={CashDollarIcon} tone="inherit" />
            {/*
              El punto: la señal de que hay algo esperando plata suya. Sin número —el número está a
              un clic— porque un contador chiquito sobre un ícono se lee mal y no cambia la decisión:
              lo que importa es «hay algo», no «hay tres».
            */}
            {frenados > 0 ? (
              <span
                aria-hidden
                style={{
                  position: 'absolute', top: -2, right: -2, width: 8, height: 8,
                  borderRadius: '50%', background: colorPunto,
                }}
              />
            ) : null}
          </span>
          {/*
            🪤 EL MONTO SE MUESTRA EN EL ÍCONO, no sólo adentro del menú. Un ícono mudo obliga a
            abrirlo para saber si te alcanza — que es justo lo que este atajo viene a evitar.
          */}
          <span style={{ fontSize: 13, fontWeight: 500, lineHeight: 1 }}>
            {CURRENCY.format(saldo.saldo)}
          </span>
        </button>
      }
    >
      <Box padding="400" minWidth="260px">
        <BlockStack gap="300">
          <BlockStack gap="050">
            <Text as="span" variant="bodyXs" tone="subdued">Tu saldo en Smarty Drop</Text>
            <Text as="p" variant="headingLg" fontWeight="bold">{CURRENCY.format(saldo.saldo)}</Text>
          </BlockStack>

          {saldo.pedidos_alcanzan != null && saldo.ticket_promedio ? (
            <Text as="p" variant="bodySm" tone="subdued">
              Te alcanza para {saldo.pedidos_alcanzan}{' '}
              {saldo.pedidos_alcanzan === 1 ? 'pedido' : 'pedidos'} de tu tamaño habitual.
            </Text>
          ) : null}

          {frenados > 0 ? (
            <Box padding="300" background="bg-surface-caution" borderRadius="200">
              <BlockStack gap="100">
                <Text as="p" variant="bodySm" fontWeight="medium">
                  {frenados === 1
                    ? 'Tienes 1 pedido esperando pago'
                    : `Tienes ${frenados} pedidos esperando pago`}
                </Text>
                <Text as="p" variant="bodyXs" tone="subdued">
                  Faltan {CURRENCY.format(saldo.falta_total)} en total. Hasta entonces no salen.
                </Text>
              </BlockStack>
            </Box>
          ) : null}

          <Divider />
          <InlineStack gap="200">
            {/*
              🪤 EL BOTÓN LLEVA EL MONTO CUANDO LO SABEMOS. «Recargar» pelado deja la cuenta del
                 lado del usuario justo cuando ya la hicimos nosotros.
            */}
            <AppButton variant="primary" onClick={recargar}>
              {estado === 'atencion' ? `Recargar ${CURRENCY.format(falta)}` : 'Recargar'}
            </AppButton>
            {/* La Billetera (movimientos, retiros, cuenta) es de quien administra la plata (20260925202000). */}
            {administraLaPlata ? (
              <AppButton
                variant="plain"
                onClick={() => ir(ROUTES.DROPSHIPPING_SMARTYDROP_SALDO)}
              >
                {frenados > 0 ? 'Ver mis pedidos' : 'Ver movimientos'}
              </AppButton>
            ) : null}
          </InlineStack>
        </BlockStack>
      </Box>
    </Popover>
    {tenantId ? (
      <RecargarSaldoModal open={recargando} onClose={() => setRecargando(false)} tenantId={tenantId} saldo={saldo} sugerido={falta > 0 ? falta : undefined} />
    ) : null}
    </>
  );
}
